/**
 * @fileoverview Product service for product/variant orchestration.
 * @module lib/productService
 */

import { z } from 'zod'
import api from './apiClient'
import { variantSchema, type Variant } from './variantService'

const productSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable().optional(),
    brand: z.string().nullable().optional(),
    imageUrl: z.string().nullable().optional(),
    archived: z.boolean().nullable().optional(),
    archivedReason: z.string().nullable().optional(),
    createdAt: z.string().nullable().optional(),
    updatedAt: z.string().nullable().optional(),
    category: z
      .object({
        uuid: z.string().optional(),
        name: z.string().optional(),
        labelColor: z.string().optional(),
      })
      .nullable()
      .optional(),
    variants: z.array(variantSchema).default([]),
  })
  .passthrough()

export type Product = z.infer<typeof productSchema>

/** Schema for variant input when creating product with explicit variants */
const variantInputSchema = z.object({
  name: z.string().min(1),
  sku: z.string().optional(),
  originalCost: z.number().nullable().optional(),
  originalCurrency: z.string().nullable().optional(),
  costDate: z.string().nullable().optional(),
  imageUrl: z.string().nullable().optional(),
})

export type VariantInput = z.input<typeof variantInputSchema>

/**
 * Schema for creating a product with the Discrete Variant Pattern.
 *
 * Simple Product (no variants array):
 *   - Backend auto-creates a "discrete" variant with name = productName
 *   - The discrete variant is hidden from users (isDiscreteVariant = true)
 *   - Use sku, originalCost, originalCurrency, costDate for the discrete variant
 *
 * Product with Explicit Variants (variants array provided):
 *   - All variants are user-visible (isDiscreteVariant = false)
 *   - No hidden variant is created
 */
const createProductRequestSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  brand: z.string().optional(),
  categoryUuid: z.string().uuid().optional(),
  imageUrl: z.string().url().optional().or(z.literal('')),
  // Fields for discrete variant (simple product mode)
  sku: z.string().optional(),
  originalCost: z.number().nullable().optional(),
  originalCurrency: z.string().nullable().optional(),
  costDate: z.string().nullable().optional(),
  // Fields for explicit variants mode
  variants: z.array(variantInputSchema).optional(),
})

export type CreateProductPayload = z.input<typeof createProductRequestSchema>

const updateProductRequestSchema = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
  brand: z.string().optional(),
  category: z.string().optional(),
  categoryUuid: z.string().uuid().optional(),
  imageUrl: z.string().url().optional(),
  archived: z.boolean().optional(),
  archivedReason: z.string().optional(),
})

export type UpdateProductPayload = z.input<typeof updateProductRequestSchema>

function omitNullish<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const out: Partial<T> = {}
  for (const [key, value] of Object.entries(obj)) {
    if (value !== null && value !== undefined) {
      out[key as keyof T] = value as T[keyof T]
    }
  }
  return out
}

export async function createProduct(payload: CreateProductPayload): Promise<Product> {
  const parsed = createProductRequestSchema.parse(payload)
  // Remove nullish values to keep request clean
  const body = omitNullish(parsed)
  const response = await api.post('/products', body)
  return productSchema.parse(response.data)
}

const createVariantSchema = z.object({
  sku: z.string().optional(),
  name: z.string().min(1),
  productName: z.string().optional(),
  description: z.string().nullable().optional(),
  category: z.string().nullable().optional(),
  brand: z.string().nullable().optional(),
  model: z.string().nullable().optional(),
  originalCost: z.number().nullable().optional(),
  originalCurrency: z.string().nullable().optional(),
  costDate: z.string().nullable().optional(),
  imageUrl: z.string().nullable().optional(),
})

export type CreateVariantPayload = z.input<typeof createVariantSchema>

export async function addProductVariants(
  productId: string,
  variants: CreateVariantPayload[]
): Promise<Variant[]> {
  const payload = z.array(createVariantSchema).min(1).parse(variants)
  const response = await api.post(`/products/${productId}/variants`, payload)
  const data = response.data
  if (Array.isArray(data)) {
    return z.array(variantSchema).parse(data)
  }
  return [variantSchema.parse(data)]
}

type PagedProductResponse = {
  content: Product[]
  last?: boolean
  totalPages?: number
  totalElements?: number
}

export async function getProducts(params: {
  page?: number
  size?: number
  sort?: string
  direction?: 'asc' | 'desc'
  query?: string
}): Promise<PagedProductResponse> {
  const response = await api.get('/products', {
    params: {
      page: params.page ?? 0,
      size: params.size ?? 50,
      sort: params.sort ?? 'name',
      direction: params.direction ?? 'asc',
      ...(params.query && { query: params.query }),
    },
  })
  const data = response.data
  if (Array.isArray(data)) {
    const content = z.array(productSchema).parse(data)
    return { content, last: true, totalPages: 1, totalElements: content.length }
  }
  if (Array.isArray(data?.content)) {
    return {
      content: z.array(productSchema).parse(data.content),
      last: data.last,
      totalPages: data.totalPages,
      totalElements: data.totalElements,
    }
  }
  return { content: z.array(productSchema).parse([]), last: true, totalPages: 1, totalElements: 0 }
}

export async function getProductById(productId: number | string): Promise<Product> {
  const response = await api.get(`/products/${productId}`)
  return productSchema.parse(response.data)
}

export async function updateProduct(
  productId: number | string,
  payload: UpdateProductPayload
): Promise<Product> {
  const parsed = updateProductRequestSchema.partial().parse(payload ?? {})
  const body = omitNullish(parsed)
  const response = await api.put(`/products/${productId}`, body)
  return productSchema.parse(response.data)
}

export async function deleteProduct(
  productId: number | string,
  targetVariantId?: number
): Promise<void> {
  await api.delete(`/products/${productId}`, {
    params: targetVariantId ? { targetVariantId } : undefined,
  })
}
